import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import {
  Package,
  Clock,
  MapPin,
  Truck,
  CreditCard,
  Store,
  ArrowLeft,
  Copy,
  CheckCircle,
  AlertCircle,
  Calendar,
  Mail,
} from "lucide-react";
import { format } from "date-fns";

const OrderDetailPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bankDetails, setBankDetails] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const [orderRes, bankRes] = await Promise.all([
          axios.get(`${API}/orders/${orderId}`, { headers }),
          axios.get(`${API}/payment/bank-details`),
        ]);
        setOrder(orderRes.data);
        setBankDetails(bankRes.data);
      } catch (error) {
        toast.error("Failed to load order");
        navigate("/orders");
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId, token, navigate]);

  const copyOrderNumber = () => {
    const orderNum = order.order_number || `BC-${order.id?.slice(0, 6).toUpperCase()}`;
    navigator.clipboard.writeText(orderNum);
    setCopied(true);
    toast.success("Order number copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "bg-amber-100 text-amber-800";
      case "confirmed":
      case "ready_for_delivery":
        return "bg-blue-100 text-blue-800";
      case "processing":
        return "bg-purple-100 text-purple-800";
      case "delivered":
        return "bg-green-100 text-green-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-slate-100 text-slate-800";
    }
  };

  // Customer-facing status label: mask "ready_for_delivery" as "Confirmed"
  const getCustomerStatusLabel = (status) => {
    if (status === "ready_for_delivery") return "Confirmed";
    return status.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
  };

  if (loading) {
    return (
      <div className="container-main py-12">
        <div className="bg-white rounded-xl p-8 shadow-sm">
          <div className="skeleton h-8 w-1/3 mb-6" />
          <div className="skeleton h-4 w-1/2 mb-4" />
          <div className="skeleton h-4 w-2/3 mb-4" />
          <div className="skeleton h-32 w-full" />
        </div>
      </div>
    );
  }

  if (!order) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="order-detail-page">
      <div className="container-main py-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate("/orders")}
          className="mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Orders
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Order Header */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-3">
                    <h1 className="text-2xl font-bold text-slate-900 font-['Outfit']">
                      Order {order.order_number || `BC-${order.id?.slice(0, 6).toUpperCase()}`}
                    </h1>
                    <button
                      onClick={copyOrderNumber}
                      className="p-1 hover:bg-slate-100 rounded"
                      title="Copy order number"
                    >
                      {copied ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : (
                        <Copy className="w-4 h-4 text-slate-400" />
                      )}
                    </button>
                  </div>
                  <p className="text-sm text-slate-500 mt-1 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Placed on {format(new Date(order.created_at), "PPp")}
                  </p>
                </div>
                <div className="text-right">
                  <Badge className={getStatusColor(order.order_status)}>
                    {getCustomerStatusLabel(order.order_status)}
                  </Badge>
                  {order.payment_status === "pending" && (
                    <Badge className="ml-2 bg-amber-100 text-amber-800">
                      Payment Pending
                    </Badge>
                  )}
                </div>
              </div>

              {/* Payment Warning */}
              {order.payment_status === "pending" && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800">Action Required: Complete Payment</p>
                      <p className="text-sm text-amber-700 mt-1">
                        Please complete the bank transfer using the details below. 
                        Use your order number as the payment reference.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Order Items */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="p-4 bg-slate-50 border-b border-slate-200">
                <h2 className="font-semibold text-slate-900 flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Order Items ({order.items.length})
                </h2>
              </div>
              <div className="divide-y divide-slate-100">
                {order.items.map((item, index) => (
                  <div key={index} className="flex gap-4 p-4">
                    <img
                      src={item.image_url || "https://images.unsplash.com/photo-1542838132-92c53300491e?w=80&h=80&fit=crop"}
                      alt={item.product_name}
                      className="w-16 h-16 rounded-lg object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://images.unsplash.com/photo-1542838132-92c53300491e?w=80&h=80&fit=crop";
                      }}
                    />
                    <div className="flex-1">
                      <h3 className="font-medium text-slate-900">{item.product_name}</h3>
                      <p className="text-sm text-slate-500">{item.store_name}</p>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-sm text-slate-600">
                          Qty: {item.quantity} × ${item.price.toFixed(2)}
                        </span>
                        <span className="font-medium text-slate-900">
                          ${(item.price * item.quantity).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Delivery Details */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="font-semibold text-slate-900 flex items-center gap-2 mb-4">
                <Truck className="w-5 h-5" />
                Delivery Details
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-slate-500">Delivery Address</p>
                      <p className="font-medium text-slate-900">
                        {order.delivery_details.address}
                      </p>
                      <p className="text-slate-700">
                        {order.delivery_details.suburb}, {order.delivery_details.state} {order.delivery_details.postcode}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-slate-500">Scheduled Delivery</p>
                      <p className="font-medium text-slate-900">
                        {format(new Date(order.delivery_details.delivery_date), "PPPP")}
                      </p>
                      <p className="text-slate-700">
                        at {order.delivery_details.delivery_time}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              {order.delivery_details.special_instructions && (
                <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Special Instructions</p>
                  <p className="text-slate-700">{order.delivery_details.special_instructions}</p>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Order Summary */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="font-semibold text-slate-900 mb-4">Order Summary</h2>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Subtotal</span>
                  <span className="font-medium">${order.subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Convenience Fee</span>
                  <span className="font-medium">${order.convenience_fee.toFixed(2)}</span>
                </div>
                {order.delivery_fee > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Delivery Fee</span>
                    <span className="font-medium">${order.delivery_fee.toFixed(2)}</span>
                  </div>
                )}
                <Separator />
                <div className="flex justify-between text-lg">
                  <span className="font-bold">Total</span>
                  <span className="font-bold">${order.total.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Store Info */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Store className="w-5 h-5" />
                Stores ({order.stores.length})
              </h2>
              <p className="text-sm text-slate-600">
                This order includes items from {order.stores.length} different store(s).
                Delivery may be staggered depending on store availability.
              </p>
            </div>

            {/* Bank Details */}
            {bankDetails && order.payment_status === "pending" && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Payment Details
                </h2>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Bank</span>
                    <span className="font-medium">{bankDetails.bank_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Account Name</span>
                    <span className="font-medium">{bankDetails.account_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">BSB</span>
                    <span className="font-medium">{bankDetails.bsb}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Account Number</span>
                    <span className="font-medium">{bankDetails.account_number}</span>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-amber-50 rounded-lg">
                  <p className="text-xs text-amber-800">
                    <strong>Reference:</strong> {order.id.slice(-8).toUpperCase()}
                  </p>
                  <p className="text-xs text-amber-700 mt-1">
                    {bankDetails.note}
                  </p>
                </div>
              </div>
            )}

            {/* Contact */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Need Help?
              </h2>
              <p className="text-sm text-slate-600">
                Contact us at support@bundlecart.com.au
              </p>
              <p className="text-xs text-slate-500 mt-2">
                Email support within 24 hours
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailPage;
